// ---------------------------------------------------------------------------
// DMARCopolo API client
// All requests use cookies for auth (credentials: 'include').
// Base URL is relative so the Vite proxy handles routing in dev.
// ---------------------------------------------------------------------------

// -- Types ------------------------------------------------------------------

export interface ApiError {
  message: string;
  code?: string;
  status: number;
}

export interface User {
  id: string;
  email: string;
  name?: string;
  role: 'owner' | 'admin' | 'analyst' | 'viewer';
  tenants: { id: string; name: string; role: string }[];
  currentTenantId: string;
}

export interface Tenant {
  id: string;
  name: string;
  plan: string;
  domainCount: number;
  createdAt: string;
}

export interface Domain {
  id: string;
  domain: string;
  status: 'pending' | 'active' | 'error';
  createdAt: string;
  lastReportAt?: string;
}

export interface DashboardSummary {
  totalEmails: number;
  passRate: number;
  failCount: number;
  domainCount: number;
  topSources: { source: string; count: number; passRate: number }[];
  trendData: { date: string; pass: number; fail: number }[];
}

export interface Report {
  id: string;
  domainId: string;
  orgName: string;
  dateRange: { begin: string; end: string };
  recordCount: number;
  passCount: number;
  failCount: number;
  createdAt: string;
}

export interface Source {
  id: string;
  sourceIp: string;
  orgName?: string;
  count: number;
  passRate: number;
  disposition: string;
  identified: boolean;
  label?: string;
}

export interface DnsStatus {
  domainId: string;
  domain: string;
  spf: { valid: boolean; record?: string; issues: string[] };
  dkim: { valid: boolean; selectors: string[]; issues: string[] };
  dmarc: { valid: boolean; record?: string; policy?: string; issues: string[] };
}

export interface DnsChange {
  id: string;
  domainId: string;
  recordType: string;
  field: string;
  oldValue: string;
  newValue: string;
  detectedAt: string;
}

export interface AuditEntry {
  id: string;
  userId: string;
  userEmail: string;
  action: string;
  resource: string;
  resourceId?: string;
  details?: Record<string, unknown>;
  createdAt: string;
}

export interface BillingInfo {
  plan: string;
  status: string;
  currentPeriodEnd: string;
  usage: { domains: number; maxDomains: number; reports: number };
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminStats {
  tenantCount: number;
  userCount: number;
  domainCount: number;
  reportCount: number;
  activeLastDay: number;
}

export interface AdminTenantDetail extends Tenant {
  users: { id: string; email: string; role: string }[];
  domains: Domain[];
}

export interface AdminConfigEntry {
  key: string;
  value: string;
  description?: string;
  updatedAt: string;
}

// -- Helpers ----------------------------------------------------------------

class ApiRequestError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const opts: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (body !== undefined) {
    opts.body = JSON.stringify(body);
  }

  const res = await fetch(path, opts);

  if (!res.ok) {
    let errBody: { message?: string; code?: string } = {};
    try {
      errBody = await res.json();
    } catch {
      // response may not be JSON
    }
    throw new ApiRequestError(
      errBody.message ?? `Request failed: ${res.status}`,
      res.status,
      errBody.code,
    );
  }

  // 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

function get<T>(path: string): Promise<T> {
  return request<T>('GET', path);
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body);
}

function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PUT', path, body);
}

function del<T>(path: string): Promise<T> {
  return request<T>('DELETE', path);
}

function qs(params: object): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

// -- API namespace ----------------------------------------------------------

export const api = {
  // -- Auth ---------------------------------------------------------------
  auth: {
    signup(email: string, password: string) {
      return post<{ user: User }>('/api/auth/signup', { email, password });
    },
    login(email: string, password: string) {
      return post<{ user: User }>('/api/auth/login', { email, password });
    },
    logout() {
      return post<void>('/api/auth/logout');
    },
    me() {
      return get<{ user: User }>('/api/auth/me');
    },
  },

  // -- Onboarding ---------------------------------------------------------
  onboarding: {
    createTenant(name: string) {
      return post<{ tenant: Tenant }>('/api/tenants', { name });
    },
  },

  // -- Dashboard ----------------------------------------------------------
  dashboard: {
    summary() {
      return get<DashboardSummary>('/api/dashboard/summary');
    },
  },

  // -- Domains ------------------------------------------------------------
  domains: {
    list() {
      return get<{ domains: Domain[] }>('/api/domains');
    },
    create(domain: string) {
      return post<{ domain: Domain }>('/api/domains', { domain });
    },
    get(id: string) {
      return get<{ domain: Domain }>(`/api/domains/${id}`);
    },
    delete(id: string) {
      return del<void>(`/api/domains/${id}`);
    },
  },

  // -- Reports ------------------------------------------------------------
  reports: {
    list(params: PaginationParams & { domainId?: string } = {}) {
      return get<PaginatedResponse<Report>>(`/api/reports${qs(params)}`);
    },
    get(id: string) {
      return get<{ report: Report }>(`/api/reports/${id}`);
    },
    summary(params: { domainId?: string; days?: number } = {}) {
      return get<DashboardSummary>(`/api/reports/summary${qs(params)}`);
    },
  },

  // -- Sources ------------------------------------------------------------
  sources: {
    list(params: PaginationParams & { domainId?: string } = {}) {
      return get<PaginatedResponse<Source>>(`/api/sources${qs(params)}`);
    },
    update(id: string, data: { label?: string; identified?: boolean }) {
      return put<{ source: Source }>(`/api/sources/${id}`, data);
    },
  },

  // -- DNS ----------------------------------------------------------------
  dns: {
    status(domainId?: string) {
      const path = domainId ? `/api/dns/status?domainId=${domainId}` : '/api/dns/status';
      return get<{ statuses: DnsStatus[] }>(path);
    },
    changes(params: PaginationParams & { domainId?: string } = {}) {
      return get<PaginatedResponse<DnsChange>>(`/api/dns/changes${qs(params)}`);
    },
  },

  // -- Audit --------------------------------------------------------------
  audit: {
    list(params: PaginationParams & { action?: string; userId?: string } = {}) {
      return get<PaginatedResponse<AuditEntry>>(`/api/audit${qs(params)}`);
    },
  },

  // -- Settings / Billing -------------------------------------------------
  settings: {
    billing() {
      return get<BillingInfo>('/api/settings/billing');
    },
  },

  // -- Admin (__admin/:secret) -------------------------------------------
  admin: {
    stats(secret: string) {
      return get<AdminStats>(`/__admin/${secret}/stats`);
    },
    tenants(secret: string) {
      return get<{ tenants: Tenant[] }>(`/__admin/${secret}/tenants`);
    },
    tenantDetail(secret: string, id: string) {
      return get<AdminTenantDetail>(`/__admin/${secret}/tenants/${id}`);
    },
    updateTenant(secret: string, id: string, data: Partial<Tenant>) {
      return put<{ tenant: Tenant }>(`/__admin/${secret}/tenants/${id}`, data);
    },
    users(secret: string) {
      return get<{ users: User[] }>(`/__admin/${secret}/users`);
    },
    updateUser(secret: string, id: string, data: Partial<User>) {
      return put<{ user: User }>(`/__admin/${secret}/users/${id}`, data);
    },
    config(secret: string) {
      return get<{ config: AdminConfigEntry[] }>(`/__admin/${secret}/config`);
    },
    updateConfig(secret: string, key: string, value: string) {
      return put<{ entry: AdminConfigEntry }>(`/__admin/${secret}/config/${key}`, { value });
    },
    audit(secret: string, params: PaginationParams & { action?: string } = {}) {
      return get<PaginatedResponse<AuditEntry>>(`/__admin/${secret}/audit${qs(params)}`);
    },
  },
} as const;
